import { useId, useState, type ReactNode } from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The candidate gate's one disclosure: a bordered row that opens a panel
 * underneath. Shared by the Welcome step's two optional checks (camera,
 * connection) and the browser-setup step's per-browser instructions (UX-17).
 *
 * The panel is conditionally RENDERED, never portalled: it has to stay inside
 * the candidate `translate="no"` subtree, or Chrome's translator rewrites its
 * text nodes and React's `removeChild` throws when it closes. Collapsing
 * unmounts the panel, not the caller — state the caller holds survives.
 */
export function Disclosure({
  label,
  icon: Icon,
  children,
  panelClassName,
}: {
  label: ReactNode;
  icon?: LucideIcon;
  children: ReactNode;
  panelClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="rounded-md border border-border bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-4 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
      >
        {/* B19: `text-left` + a non-shrinking icon. Without them the
            label centred itself once it wrapped at 375, while at 1440
            it sits flush after the icon. */}
        <span className="flex min-w-0 items-center gap-2 text-left font-medium">
          {Icon && (
            <Icon
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
          {label}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          id={panelId}
          className={cn(
            "space-y-2 border-t border-border px-4 py-3 text-sm",
            panelClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
